import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import NavBar from '../components/NavBar';

const SECTIONS = [
  { key: 'visit', title: 'Visit Info', desc: 'Arrival, departure, shift, location.', required: true },
  { key: 'status', title: 'Overall Status', desc: 'Doing well / slight / significant / urgent + appearance.', required: true },
  { key: 'vitals', title: 'Vitals', desc: 'BP, pulse, temp, SpO₂, weight, glucose.' },
  { key: 'mental', title: 'Mental & Cognitive Status', desc: 'Alertness, orientation, confusion — Phase 2.' },
  { key: 'mood', title: 'Mood & Behavior', desc: 'Overall mood + behaviors observed.' },
  { key: 'pain', title: 'Pain Assessment', desc: '0-10 scale + location + action taken.' },
  { key: 'meals', title: 'Meals & Nutrition', desc: 'Breakfast/lunch/dinner + appetite.' },
  { key: 'fluids', title: 'Fluid Intake', desc: 'Water, other fluids — Phase 2.' },
  { key: 'meds', title: 'Medication Status', desc: 'Documented per care plan — Phase 2.' },
  { key: 'mobility', title: 'Mobility', desc: 'Independence level, aids used — Phase 2.' },
  { key: 'adls', title: 'ADLs', desc: 'Bathing, dressing, grooming, toileting — Phase 2.' },
  { key: 'toileting', title: 'Toileting & Elimination', desc: 'Urination, bowel movement — Phase 2.' },
  { key: 'skin', title: 'Skin & Body Check', desc: 'Redness, wounds, pressure areas — Phase 2.' },
  { key: 'sleep', title: 'Sleep', desc: 'Quality, hours, disturbances — Phase 2.' },
  { key: 'activities', title: 'Activities & Social', desc: 'Walking, therapy, visits — Phase 2.' },
  { key: 'fall', title: 'Fall / Incident Check', desc: 'Auto-opens full Fall Report when YES.', required: true },
  { key: 'concerns', title: 'Caregiver Concerns', desc: 'Anything family/care team should know — Phase 2.' },
  { key: 'summary', title: 'Daily Summary & Sign', desc: 'Narrative + signature.', required: true },
];

const VITALS = [
  { key: 'bp', title: 'Blood Pressure', unit: 'mmHg', threshold: 'compound' },
  { key: 'pulse', title: 'Pulse', unit: 'bpm' },
  { key: 'temperature', title: 'Temperature', unit: '°F' },
  { key: 'rr', title: 'Respiratory Rate', unit: 'br/min' },
  { key: 'spo2', title: 'Oxygen Saturation (SpO₂)', unit: '%' },
  { key: 'weight', title: 'Weight', unit: 'lb' },
  { key: 'glucose', title: 'Blood Glucose', unit: 'mg/dL' },
];

const CONDITIONS = [
  { key: 'diabetes', title: 'Diabetes', icon: '💉', desc: 'Glucose logs, insulin, foot check.' },
  { key: 'dementia', title: "Dementia / Alzheimer's", icon: '🧠', desc: 'Cognitive changes, wandering, sundowning.' },
  { key: 'copd', title: 'COPD / Respiratory', icon: '🫁', desc: 'Breathing, cough, sputum, oxygen.' },
  { key: 'cardiac', title: 'Heart Failure / Cardiac', icon: '❤️', desc: 'Weight, swelling, breathing, fatigue.' },
  { key: 'parkinsons', title: "Parkinson's", icon: '🎯', desc: 'Tremor, movement, balance, speech.' },
  { key: 'stroke', title: 'Stroke Recovery', icon: '🩺', desc: 'Strength, speech, swallowing, therapy.' },
];

const CarePlanBuilder = ({ user, onLogout }) => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c, p] = await Promise.all([
          axios.get(`${API}/clients/${clientId}`),
          axios.get(`${API}/clients/${clientId}/care-plan`),
        ]);
        setClient(c.data);
        setPlan(p.data);
      } catch { toast.error('Failed to load care plan'); }
      finally { setLoading(false); }
    })();
  }, [clientId]);

  const toggleSection = (key) => {
    if (SECTIONS.find(s => s.key === key)?.required) return;
    setPlan(p => ({
      ...p,
      enabled_sections: p.enabled_sections.includes(key)
        ? p.enabled_sections.filter(k => k !== key)
        : [...p.enabled_sections, key],
    }));
  };
  const toggleVital = (key) => setPlan(p => ({
    ...p,
    enabled_vitals: p.enabled_vitals.includes(key)
      ? p.enabled_vitals.filter(k => k !== key)
      : [...p.enabled_vitals, key],
  }));
  const toggleCondition = (key) => setPlan(p => ({
    ...p,
    condition_modules: p.condition_modules.includes(key)
      ? p.condition_modules.filter(k => k !== key)
      : [...p.condition_modules, key],
  }));
  const setThreshold = (vitalKey, field, val) => {
    setPlan(p => ({
      ...p,
      vital_thresholds: {
        ...p.vital_thresholds,
        [vitalKey]: {
          ...(p.vital_thresholds[vitalKey] || {}),
          [field]: val === '' ? null : parseFloat(val),
        },
      },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled_sections: plan.enabled_sections,
        enabled_vitals: plan.enabled_vitals,
        vital_thresholds: plan.vital_thresholds,
        condition_modules: plan.condition_modules,
        plan_notes: plan.plan_notes,
      };
      await axios.put(`${API}/clients/${clientId}/care-plan`, payload);
      toast.success('Care plan saved');
    } catch { toast.error('Failed to save care plan'); }
    finally { setSaving(false); }
  };

  if (loading || !plan || !client) return <div className="loading-screen">Loading care plan…</div>;

  return (
    <div className="dashboard-container" data-testid="care-plan-builder">
      <NavBar onLogout={onLogout} />
      <div className="dashboard-content page-container">
        <button onClick={() => navigate('/clients')} className="clog-back-btn" data-testid="cp-back-btn">
          <i className="fa-solid fa-arrow-left" /> Back to Clients
        </button>
        <div style={{ marginTop: '0.5rem', marginBottom: '1rem' }}>
          <h1 className="page-title">
            <i className="fa-solid fa-clipboard-user" style={{ color: '#2c5364', marginRight: 8 }} />
            Care Plan Builder
          </h1>
          <p className="page-subtitle">
            for <strong>{client.name}</strong> — choose which daily-log sections, vitals, and condition modules apply to this client.
          </p>
        </div>

        {/* Sections */}
        <div className="section-card cp-section">
          <h2 className="cp-section-title"><i className="fa-solid fa-list-check" /> Daily Log Sections</h2>
          <p className="cp-section-desc">Enable only the sections a caregiver needs to complete for this client. Required sections cannot be turned off.</p>
          <div className="cp-grid">
            {SECTIONS.map(s => {
              const active = plan.enabled_sections.includes(s.key);
              return (
                <label
                  key={s.key}
                  className={`cp-toggle-card ${active ? 'active' : ''} ${s.required ? 'required' : ''}`}
                  data-testid={`cp-section-${s.key}`}
                >
                  <input
                    type="checkbox"
                    checked={active}
                    disabled={s.required}
                    onChange={() => toggleSection(s.key)}
                    data-testid={`cp-section-toggle-${s.key}`}
                  />
                  <div>
                    <div className="cp-toggle-title">
                      {s.title}
                      {s.required && <span className="cp-req-pill">Required</span>}
                    </div>
                    <div className="cp-toggle-desc">{s.desc}</div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {/* Vitals + Thresholds */}
        <div className="section-card cp-section">
          <h2 className="cp-section-title"><i className="fa-solid fa-stethoscope" /> Vitals to Collect</h2>
          <p className="cp-section-desc">
            Choose which vitals the caregiver records. Optional thresholds flag values <em>outside the plan range</em> —
            CareGuard never labels them clinically; interpretation stays with the care plan/provider.
          </p>
          <div className="cp-vitals-list">
            {VITALS.map(v => {
              const active = plan.enabled_vitals.includes(v.key);
              const isCompound = v.threshold === 'compound';
              return (
                <div key={v.key} className={`cp-vital-row ${active ? 'active' : ''}`} data-testid={`cp-vital-${v.key}`}>
                  <label className="cp-vital-toggle">
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => toggleVital(v.key)}
                      data-testid={`cp-vital-toggle-${v.key}`}
                    />
                    <span className="cp-vital-name">{v.title}</span>
                    <span className="cp-vital-unit">{v.unit}</span>
                  </label>
                  {active && !isCompound && (
                    <div className="cp-threshold-inputs">
                      <label>Min <input type="number" step="0.1" value={plan.vital_thresholds[v.key]?.min ?? ''}
                        onChange={e => setThreshold(v.key, 'min', e.target.value)} data-testid={`cp-threshold-min-${v.key}`} /></label>
                      <label>Max <input type="number" step="0.1" value={plan.vital_thresholds[v.key]?.max ?? ''}
                        onChange={e => setThreshold(v.key, 'max', e.target.value)} data-testid={`cp-threshold-max-${v.key}`} /></label>
                    </div>
                  )}
                  {active && isCompound && v.key === 'bp' && (
                    <div className="cp-threshold-inputs" style={{ flexWrap: 'wrap' }}>
                      <label>Sys Min <input type="number" value={plan.vital_thresholds.bp_sys?.min ?? ''}
                        onChange={e => setThreshold('bp_sys', 'min', e.target.value)} /></label>
                      <label>Sys Max <input type="number" value={plan.vital_thresholds.bp_sys?.max ?? ''}
                        onChange={e => setThreshold('bp_sys', 'max', e.target.value)} /></label>
                      <label>Dia Min <input type="number" value={plan.vital_thresholds.bp_dia?.min ?? ''}
                        onChange={e => setThreshold('bp_dia', 'min', e.target.value)} /></label>
                      <label>Dia Max <input type="number" value={plan.vital_thresholds.bp_dia?.max ?? ''}
                        onChange={e => setThreshold('bp_dia', 'max', e.target.value)} /></label>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Condition modules */}
        <div className="section-card cp-section">
          <h2 className="cp-section-title"><i className="fa-solid fa-notes-medical" /> Condition-Specific Modules</h2>
          <p className="cp-section-desc">
            Enable the modules matching this client's conditions. They'll appear as extra steps in the caregiver's daily log <em>(built in Phase 3)</em>.
          </p>
          <div className="cp-grid">
            {CONDITIONS.map(c => {
              const active = plan.condition_modules.includes(c.key);
              return (
                <label
                  key={c.key}
                  className={`cp-toggle-card ${active ? 'active' : ''}`}
                  data-testid={`cp-condition-${c.key}`}
                >
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={() => toggleCondition(c.key)}
                    data-testid={`cp-condition-toggle-${c.key}`}
                  />
                  <div>
                    <div className="cp-toggle-title">
                      <span style={{ fontSize: '1.15rem', marginRight: 6 }}>{c.icon}</span>
                      {c.title}
                    </div>
                    <div className="cp-toggle-desc">{c.desc}</div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {/* Plan notes */}
        <div className="section-card cp-section">
          <h2 className="cp-section-title"><i className="fa-solid fa-pen-to-square" /> Plan Notes</h2>
          <textarea
            rows={3}
            placeholder="Any additional guidance for caregivers — e.g., 'Client uses walker outdoors only', 'Prefers meals cut into small pieces', 'Family calls each evening at 7pm'."
            value={plan.plan_notes || ''}
            onChange={e => setPlan(p => ({ ...p, plan_notes: e.target.value }))}
            data-testid="cp-notes-input"
          />
        </div>

        <div className="cp-save-bar">
          <button
            className="submit-button"
            style={{ width: 'auto', padding: '0.85rem 2rem' }}
            onClick={save}
            disabled={saving}
            data-testid="cp-save-btn"
          >
            {saving ? 'Saving…' : <><i className="fa-solid fa-floppy-disk" /> Save Care Plan</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CarePlanBuilder;
